import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Box, Chip, Collapse, IconButton, Paper, Stack, Typography } from '@mui/material';
import React from 'react';

import { BoardContract, BoardTrafficker } from '../../api/ach/board';
import AlertRow from './AlertRow';
import ruleLabel from './ruleLabels';
import { SEVERITY_META, SEVERITY_ORDER } from './severity';

interface ContractCardProps {
  contract: BoardContract;
  onAckIssue: (issueId: number) => void;
  onAckGroup: (ruleId: string, contractId: number) => void;
  // True on the Resolved tab: rows are read-only, no ack controls.
  readOnly?: boolean;
  // Disables ack controls while any ack mutation is in flight (guards double-submit).
  acking?: boolean;
}

// bulkAckRules returns the rule ids that appear on 2+ open issues within this
// contract — those get a single "Ack all <rule> on this contract" action.
function bulkAckRules(contract: BoardContract): string[] {
  const counts = new Map<string, number>();
  contract.line_items.forEach((li) =>
    li.issues.forEach((issue) => {
      if (issue.state === 'open') {
        counts.set(issue.rule_id, (counts.get(issue.rule_id) ?? 0) + 1);
      }
    })
  );
  return [...counts.entries()].filter(([, n]) => n >= 2).map(([rule]) => rule);
}

// rollupTrafficker inspects the trafficker across EVERY issue in the contract.
// `uniform` is true when they all share one id (null = "unassigned" counts as a value):
// uniform groups omit the per-row trafficker. `person` is the trafficker to show once
// on the header — set only for a uniform, assigned group (null for mixed groups and for
// uniformly-unassigned groups, which have nobody to show).
function rollupTrafficker(contract: BoardContract): { uniform: boolean; person: BoardTrafficker | null } {
  let seen: BoardTrafficker | null = null;
  let count = 0;
  const distinct = new Set<number | null>();
  contract.line_items.forEach((li) =>
    li.issues.forEach((issue) => {
      distinct.add(issue.trafficker?.id ?? null);
      count += 1;
      if (issue.trafficker) {
        seen = issue.trafficker;
      }
    })
  );
  const uniform = count > 0 && distinct.size === 1;
  return { uniform, person: uniform ? seen : null };
}

function LineItemSection({
  ioName,
  insertionOrderId,
  contract,
  issues,
  onAckIssue,
  acking,
  showTrafficker,
}: {
  ioName: string;
  insertionOrderId: number;
  contract: BoardContract;
  issues: BoardContract['line_items'][number]['issues'];
  onAckIssue: (issueId: number) => void;
  acking: boolean;
  showTrafficker: boolean;
}) {
  const [open, setOpen] = React.useState(true);
  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ cursor: 'pointer', px: 1, py: 0.5 }}
        onClick={() => setOpen((v) => !v)}
      >
        <IconButton size="small" sx={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle2">{ioName}</Typography>
        <Typography variant="caption" color="text.secondary">
          ({issues.length})
        </Typography>
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Box pl={2}>
          {issues.map((issue) => (
            <AlertRow
              key={issue.id}
              issue={issue}
              contractId={contract.contract_id}
              insertionOrderId={insertionOrderId}
              ioName={ioName}
              onAck={onAckIssue}
              acking={acking}
              showTrafficker={showTrafficker}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

// ContractCard renders one contract group: a header with the contract name and its
// severity-count chips, an optional bulk-ack toolbar (rules hit on 2+ open issues),
// and one collapsible section per line item (falls back to "IO <id>" when unnamed).
function ContractCard({ contract, onAckIssue, onAckGroup, readOnly = false, acking = false }: ContractCardProps) {
  const bulkRules = readOnly ? [] : bulkAckRules(contract);
  const { uniform, person } = rollupTrafficker(contract);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }} data-testid={`contract-card-${contract.contract_id}`}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" mb={1}>
        <Typography variant="h6">{contract.contract_name}</Typography>
        {person && (
          <Stack
            direction="row"
            spacing={0.25}
            alignItems="center"
            data-testid={`contract-trafficker-${contract.contract_id}`}
          >
            <PersonOutlineIcon sx={{ fontSize: 16 }} color="disabled" />
            <Typography variant="body2" color="text.secondary">
              {person.name}
            </Typography>
          </Stack>
        )}
        <Stack direction="row" spacing={0.5}>
          {SEVERITY_ORDER.filter((sev) => contract.severity_counts[sev] > 0).map((sev) => (
            <Chip
              key={sev}
              size="small"
              color={SEVERITY_META[sev].color}
              variant="outlined"
              label={`${SEVERITY_META[sev].label} ${contract.severity_counts[sev]}`}
            />
          ))}
        </Stack>
      </Stack>

      {bulkRules.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" mb={1} data-testid="bulk-ack-toolbar">
          {bulkRules.map((rule) => (
            <Chip
              key={rule}
              size="small"
              color="primary"
              variant="outlined"
              disabled={acking}
              onClick={() => onAckGroup(rule, contract.contract_id)}
              label={`Ack all “${ruleLabel(rule)}” on this contract`}
            />
          ))}
        </Stack>
      )}

      {contract.line_items.map((li) => (
        <LineItemSection
          key={li.insertion_order_id}
          ioName={li.io_name || `IO ${li.insertion_order_id}`}
          insertionOrderId={li.insertion_order_id}
          contract={contract}
          issues={li.issues}
          onAckIssue={onAckIssue}
          acking={acking}
          showTrafficker={!uniform}
        />
      ))}
    </Paper>
  );
}

export default ContractCard;
